import { PureComponent } from 'react';
import { connect } from 'react-redux';

import find from 'lodash/find';
import isEmpty from 'lodash/isEmpty';
import PropTypes from 'prop-types';
import { change, getFormValues } from 'redux-form';

import VisitorFormComponent from './VisitorFormComponent';

import { getDoors } from '../AccessPoint/actionCreators';
import {
  getBuilding,
  getBuildingUnits,
  getBuildingsForFilter,
} from '../Building/actionCreators';
import { getUnitResidents } from '../Unit/actionCreators';
import {
  clearExistingVisitor,
  clearInviteInfo,
  confirmToBePermanent,
  getVisitorInfoById,
  getVisitorsList,
  onPersonSubmit,
  onSubmitComplete,
  sendInvite,
} from './actionCreators';

import {
  filterObjectByIds,
  filterObjectByKeyValue,
  formatDate,
} from '../../utils';
import { findPersonEmail, findPersonPhoneNumber } from '../Person/utils';
import { formatTimeRange } from '../Schedule/utils';
import {
  formatDoorName,
  getPreselectedInitialValues,
  getVisitorFormData,
} from './utils';

import { history } from '../../store';

const form = 'VisitorForm';

class VisitorFormContainer extends PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      initialValues: getPreselectedInitialValues(props),
      isWarningShown: false,
      visitor: null,
    };
    this.onSubmit = this.onSubmit.bind(this);
    this.completeVisitor = this.completeVisitor.bind(this);
    this.showWarning = this.showWarning.bind(this);
    this.closeWarning = this.closeWarning.bind(this);
    this.getInitialValues = this.getInitialValues.bind(this);
  }

  async componentDidMount() {
    const { initialValues = {} } = this.state;
    const {
      visitorId,
      getBuildings,
      onBuildingChange,
      onUnitChange,
      getVisitorInfoById,
    } = this.props;
    const { building, unit } = initialValues;

    building && onBuildingChange(building);
    unit && onUnitChange(unit);
    !building && (await getBuildings());

    if (visitorId) {
      const visitor = await getVisitorInfoById(visitorId);
      this.getInitialValues(visitor);
    }
  }

  async componentDidUpdate(prevProps) {
    const { initialValues = {} } = this.state;
    const {
      completeData,
      formValues,
      onBuildingChange,
      onUnitChange,
      visitorId,
      updateFormValue,
    } = this.props;
    const { building, unit } = initialValues;
    if (!isEmpty(completeData) && prevProps?.completeData !== completeData) {
      this.showWarning();
    }

    const prevBuilding = prevProps?.formValues?.building;
    const currentBuilding = formValues?.building;
    if (!building && currentBuilding && prevBuilding !== currentBuilding) {
      await onBuildingChange(currentBuilding);
      updateFormValue('unit', null);
      updateFormValue('authorizer', null);
      return null;
    }

    const prevUnit = prevProps?.formValues?.unit;
    const currentUnit = formValues?.unit;
    if (!unit && !visitorId && currentUnit && prevUnit !== currentUnit) {
      await onUnitChange(currentUnit);
      updateFormValue('authorizer', null);
    }
    return null;
  }

  async onSubmit(data) {
    const { onSubmit, sendInvite } = this.props;

    const formattedData = getVisitorFormData(data);
    const { sendElectronicKey, visitorData, inviteData } = formattedData || {};

    const visitor = await onSubmit(visitorData);

    sendElectronicKey && visitor && (await sendInvite(inviteData, visitor?.id));
    visitor && history.goBack();
  }

  getInitialValues(visitor = {}) {
    const {
      id,
      building,
      person,
      authorizer,
      creator,
      validUntil,
      comments,
      allowedTimeStart,
      allowedTimeEnd,
    } = visitor;
    const { visitorId } = this.props;
    if (!visitorId || !id) return;

    const email = findPersonEmail(person);
    const phoneNumber = findPersonPhoneNumber(person);
    const unit = visitor?.unitName || 'Building visitor';
    const authorizerName = `${authorizer?.firstName} ${authorizer?.lastName}`;
    const creatorName = `${creator?.firstName ?? 'Carson'} ${
      creator?.lastName ?? 'Admin'
    }`;

    const initialValues = {
      building,
      firstName: person?.firstName,
      lastName: person?.lastName,
      email,
      phoneNumber,
      unit,
      contactPerson: authorizer ? authorizerName : creatorName,
      avatar: person?.photo?.url,
      validUntil: validUntil?.substring(0, 16),
      validUntilInfo: formatDate(validUntil, false) || 'Permanent visitor',
      comments,
      allowedTimeStart: allowedTimeStart && formatTimeRange(allowedTimeStart),
      allowedTimeEnd: allowedTimeEnd && formatTimeRange(allowedTimeEnd),
    };

    this.setState({
      initialValues,
      visitor,
    });
  }

  async completeVisitor(e) {
    const { completeData, onSubmitComplete, formValues, sendInvite } =
      this.props;
    const chosenVisitor = !isEmpty(completeData)
      ? find(completeData, (data) => data.id === Number(e.id))
      : [];

    const formattedData = getVisitorFormData(formValues);
    const { sendElectronicKey, visitorData, inviteData } = formattedData || {};
    const person = chosenVisitor?.id;

    const visitor = await onSubmitComplete({
      ...visitorData,
      ...chosenVisitor,
      person,
    });
    sendElectronicKey && visitor && (await sendInvite(inviteData, visitor?.id));
    visitor && history.goBack();
  }

  showWarning() {
    this.setState({
      isWarningShown: true,
    });
  }

  closeWarning() {
    this.setState({
      isWarningShown: false,
    });
  }

  render() {
    const { initialValues, isWarningShown, visitor } = this.state;
    const {
      visitorId,
      selectedBuilding,
      buildingDoors,
      filteredBuildings,
      filteredUnits,
      visitorPhotoRequired,
      getVisitors,
      shouldBePermanent,
      updateFormValue,
      formValues,
    } = this.props;

    const props = {
      showWarning: this.showWarning,
      closeWarning: this.closeWarning,
      onSubmit: this.onSubmit,
      completeVisitor: this.completeVisitor,
      updateFormValue,

      initialValues,
      isWarningShown,
      visitor,

      visitorId,
      selectedBuilding,
      buildingDoors,
      filteredBuildings,
      filteredUnits,

      getVisitors,
      shouldBePermanent,
      visitorPhotoRequired,

      form,
      formValues,
    };
    return <VisitorFormComponent {...props} />;
  }
}

VisitorFormContainer.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  visitorId: PropTypes.string,
  getBuildings: PropTypes.func.isRequired,
  onBuildingChange: PropTypes.func.isRequired,
  onUnitChange: PropTypes.func.isRequired,
  getVisitorInfoById: PropTypes.func.isRequired,
  completeData: PropTypes.instanceOf(Array).isRequired,
  formValues: PropTypes.instanceOf(Object),
  updateFormValue: PropTypes.func.isRequired,
  sendInvite: PropTypes.func.isRequired,
  onSubmitComplete: PropTypes.func.isRequired,
  selectedBuilding: PropTypes.instanceOf(Object),
  buildingDoors: PropTypes.instanceOf(Array).isRequired,
  filteredBuildings: PropTypes.instanceOf(Object).isRequired,
  filteredUnits: PropTypes.instanceOf(Object).isRequired,
  visitorPhotoRequired: PropTypes.bool.isRequired,
  getVisitors: PropTypes.func.isRequired,
  shouldBePermanent: PropTypes.func.isRequired,
};

VisitorFormContainer.defaultProps = {
  selectedBuilding: [],
};

const mapStateToProps = (state, ownProps) => {
  const { match } = ownProps;
  const { visitors, buildings, units, doors, selectExisting } = state;

  const pmcId = match?.params?.PMCId;
  const buildingId = match?.params?.buildingId;
  const visitorId = match?.params?.visitorId;

  const formValues = getFormValues(form)(state);

  const filteredBuildings = pmcId
    ? filterObjectByKeyValue(buildings, 'parent.id', Number(pmcId))
    : buildings;
  const selectedBuildingId = buildingId || formValues?.building;
  const selectedBuilding =
    selectedBuildingId && filteredBuildings?.[selectedBuildingId];

  const filteredUnits = selectedBuildingId
    ? filterObjectByIds(units, selectedBuilding?.units)
    : {};

  const visitorPhotoRequired =
    selectedBuilding?.visitorKeysData?.visitorPhotoRequired ?? false;
  const completeData = selectExisting?.people;

  const buildingDoors = [...doors].filter(
    ({ building }) => Number(building) === Number(selectedBuildingId),
  );

  const formattedBuildingDoors = formatDoorName(buildingDoors);

  return {
    visitorId,
    selectedBuilding,
    buildingDoors: formattedBuildingDoors,
    filteredBuildings,
    filteredUnits,
    visitorPhotoRequired,
    completeData,
    formValues,
    visitors: visitors?.results ?? [],
  };
};

const mapDispatchToProps = (dispatch, ownProps) => ({
  getVisitorInfoById: (visitorId) => getVisitorInfoById(dispatch, visitorId),
  onSubmit: (data) => onPersonSubmit(dispatch, data, 'Visitor', ownProps),
  onSubmitComplete: (data) =>
    onSubmitComplete(dispatch, data, 'Visitor', ownProps),
  getBuildings: () => getBuildingsForFilter(dispatch),

  onBuildingChange: (buildingId) => {
    getBuilding(dispatch, buildingId);
    getBuildingUnits(dispatch, {}, buildingId);
    getDoors(dispatch, {}, buildingId);
  },
  onUnitChange: (unitId) => unitId && getUnitResidents(dispatch, unitId),

  clearExistingVisitor: () => clearExistingVisitor(dispatch),
  getVisitors: (queryParams) => getVisitorsList(dispatch, queryParams),
  shouldBePermanent: () => confirmToBePermanent(dispatch, 'Visitors'),
  sendInvite: (data, visitorId, hasInvite, queryParams) =>
    sendInvite(dispatch, data, visitorId, hasInvite, queryParams),
  clearInviteInfo: () => clearInviteInfo(dispatch),
  updateFormValue: (field, value) => dispatch(change(form, field, value)),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(VisitorFormContainer);
